/* eslint-disable nonblock-statement-body-position */
import { useState, useEffect } from "react";
import PageTitle from "../../../components/PageTitle";
import { Grid } from "@mui/material";
import useStyles from "../styles";
import QuestionCard from "../../studentexam/components/QuestionCard";
import { questionDisplayIndex } from "../../../utils/questionDisplayIndex";
import PageLoader from "../../../components/PageLoader";
import "react-circular-progressbar/dist/styles.css";
import { useParams, useNavigate } from "react-router-dom";
import { httprequest } from "../../../data/api";
// import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";

const ASSIGNMENT_ENDPOINT = "/api/v1/assignment/";
const SUBMIT_ENDPOIINT = "/api/v1/assignment/submit/";

const EnrolAssignment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [studentAssignment, setStudentAssignment] = useState({});
  const classes = useStyles();
  // const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  // const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const [isExamStarted, setIsExamStarted] = useState(false);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [leaveModal, setLeaveModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedAnswer, setSelectedAnswer] = useState({
    answers: []
  });

  /**
 * Opens the modal to confirm if the student wants to leave the exam.
 *
 */
  const handleLeaveExamModal = () => {
    setLeaveModal(true);
  };

  /**
 * Closes the leave exam confirmation modal.
 *
 */
  const handleCloseLeaveModal = () => {
    setLeaveModal(false);
  };

  /**
 * Advances to the next question in the assignment.
 * Ensures the index stays within the bounds of the question list.
 *
 */
  const handleNext = () => {
    if (studentAssignment?.questions && activeQuestionIndex < studentAssignment.questions.length - 1) {
      setActiveQuestionIndex(prevIndex => prevIndex + 1);
    }
  };

  /**
 * Navigates to the previous question in the assignment.
 * Prevents going to a negative index.
 *
 */
  const handlePrevious = () => {
    if (activeQuestionIndex > 0) {
      setActiveQuestionIndex(prevIndex => prevIndex - 1);
    }
  };

  /**
 * Clears cached student assignment data and navigates back to the assignments list.
 * Adds a small delay before navigation to allow UI transitions or user feedback.
 */
  const handleNavigate = () => {
    localStorage.removeItem("studentAssignmentCache");
    setTimeout(() => {
      navigate("/assesments/assignments");
    }, 1000);
  };

  /**
 * useEffect - Runs once on component mount.
 * Fetches assignment data from the server and populates student answers.
 * If the assignment is expired, displays a toast and redirects the student.
 */
  useEffect(() => {
    const fetchExam = async () => {
      try {
        setIsLoading(true);
        const data = await httprequest.get(`${ASSIGNMENT_ENDPOINT}${id}`);
        setStudentAssignment(data?.data?.data);
        setSelectedAnswer({
          answers: data?.data?.data?.questions.map((question) => ({
            question_index: question.question_index,
            answer: question?.student_answer
          }))
        });
        if (data?.data?.data?.status === "expired") {
          toast.error("This assignment has expired, please contact your instructor for more information.");
          setTimeout(() => {
            handleNavigate();
          }, 3000);
        }
      } catch (err) {
        toast.error(err?.response?.data?.message);
        setTimeout(() => {
          navigate("/assesments/assignments");
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    };
    fetchExam();
  }, []);

  /**
 * Handles the selection of an answer for a specific question.
 * Updates the `studentAssignment` state, local storage, and `selectedAnswer` state accordingly.
 *
 * @param {number} questionIndex - The index of the question being answered.
 * @param {string} answer - The selected answer value.
 * @param {string} type - The type of question (e.g., "checkbox", "multiple_choice").
 *
 */
  const handleAnswerClick = (questionIndex, answer, type) => {
    // Update only the selected question's answer
    const updatedQuestions = studentAssignment.questions.map((question) => {
      if (question.question_index === questionIndex) {
        const updatedQuestion = { ...question };

        if (type === "checkbox") {
          // Initialize the answer array if not present
          if (!updatedQuestion?.student_answer) {
            updatedQuestion.student_answer = [];
          }

          const answerIndex = updatedQuestion.student_answer.indexOf(answer);
          if (answerIndex !== -1) {
            // Uncheck the answer
            updatedQuestion.student_answer.splice(answerIndex, 1);
          } else {
            // Check the answer
            updatedQuestion.student_answer.push(answer);
          }
        } else if (type === "multiple_choice") {
          // Replace the current answer for single-choice questions
          updatedQuestion.student_answer = [answer];
        }

        return updatedQuestion;
      }

      return question;
    });

    // Create the updated assignment object
    const updatedExam = {
      ...studentAssignment,
      questions: updatedQuestions,
      description: studentAssignment?.description === "" ? "Attempt questions" : studentAssignment?.description
    };

    const updatedSelectedAnswer = updatedQuestions.map((question) => ({
      question_index: question.question_index,
      answer: question?.student_answer
    }));

    // Store the updated exam in local storage
    localStorage.setItem("studentAssignmentCache", JSON.stringify(updatedExam));

    // Update the state locally to reflect changes without re-fetching
    setStudentAssignment(updatedExam);
    setSelectedAnswer({ answers: updatedSelectedAnswer });
  };

  /**
 * Submits the final answers of the exam to the server.
 * Clears local storage and navigates away after a successful or failed submission.
 *
 * @async
 * @returns {Promise<void>}
 */
  const handleFinalSubmit = async () => {
    try {
      const res = await httprequest.post(`${SUBMIT_ENDPOIINT}${id}`, selectedAnswer);
      toast.success(res?.data?.message);
      localStorage.removeItem("studentAssignmentCache");
      // Navigate after successful response
      setTimeout(() => {
        handleNavigate();
      }, 3000);
    } catch (err) {
      setTimeout(() => {
        handleNavigate();
      }, 3000);
      toast.error(err?.response?.data?.message);
    }
  };

  /**
 * Navigates to the specified question index.
 * Updates the active question state.
 *
 * @param {number} index - The index of the question to navigate to.
 *
 */
  const handleNavigateToQuestion = (index) => {
    setActiveQuestion(index);
    setActiveQuestionIndex(index);
    if (studentAssignment?.questions_display_mode === "all_at_once") {
      const questionIndex = studentAssignment?.questions[index]?.question_index;
      const idElement = document.getElementById(`question_${questionIndex}`);
      if (idElement) {
        idElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  /**
 * Checks if a given question has been answered by the student.
 *
 * @param {number} questionIndex - The index of the question to check.
 * @returns {boolean} `true` if the question has an answer, otherwise `false`.
 */
  const isQuestionAnswered = (questionIndex) => {
    return selectedAnswer?.answers.some(
      (a) => a?.question_index === questionIndex && a?.answer?.length > 0
    ) || false;
  };

  /**
 * Starts the exam by updating the `isExamStarted` state to true.
 *
 */
  const handleStartTest = () => {
    setIsExamStarted(true);
  };

  /**
 * Continues the exam (e.g., after a reload) by setting `isExamStarted` to true.
 *
 */
  const handleContinueExam = () => {
    setIsExamStarted(true);
  };

  /**
 * useEffect - Runs once on component mount to reset `isExamStarted` to false.
 * Ensures the exam is not started by default when the component is initialized.
 */
  useEffect(() => {
    setIsExamStarted(false);
  }, []);

  /**
 * useEffect - On component mount, attempts to restore cached exam state
 * from local storage and updates the `studentAssignment` state accordingly.
 */
  useEffect(() => {
    const cachedAssignment = localStorage.getItem("studentAssignmentCache");
    if (cachedAssignment) {
      setStudentAssignment(JSON.parse(cachedAssignment));
    }
  }, []);

  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    // Attach when component mounts
    document.addEventListener("contextmenu", handleContextMenu);

    // Cleanup on unmount
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  return (
    <div className={classes.testcont}>
      <div className={`${classes.mainpage} page-content`}>
        <PageTitle
          title={studentAssignment?.title || "My Assessment"}
          subtitle={studentAssignment?.instruction || "Read Instructions well before starting the assignment"}
          button={
            <div className="flex gap-10">
              <Button
                variant="dangeroutline"
                buttonSize="fluid"
                color="btndanger"
                buttonName="Leave Assignment"
                onClick={handleLeaveExamModal}
              />
              <>
                {localStorage.getItem("studentAssignmentCache") ? (
                  <Button
                    onClick={handleContinueExam}
                    variant="primary"
                    buttonName="Continue Assignment"
                    buttonSize="medium"
                    color="btndefault"
                  />
                ) : (
                  <Button
                    onClick={handleStartTest}
                    variant="primary"
                    buttonName="Start Assignment"
                    buttonSize="medium"
                    color="btndefault"
                  />
                )}
              </>
            </div>
          }
        />
        <div className="margin-top-30">
          <Grid container spacing={2}>
            <Grid item lg={9} md={9} sm={12} xs={12}>
              {/* <div className={`${classes.progressbar} padding-inline-10`}>
                <ProgressBar completed={100 - percentage} baseBgColor="#F0F2F5" bgColor="#28AE00" labelSize="12px" height="14px" />
                <div className={classes.timeleft}>
                  <h2>Time Remaining: {timer}</h2>
                </div>
              </div> */}
              {/* Fetch temporary draft */}
              <div>
                <div>
                  {isLoading ? (
                    <PageLoader />
                  ) : (
                    <>
                      {!isExamStarted ? (
                        <>
                          <div className={`${classes.getcont}`}>
                            <p className={classes.getstarted}>Please read the following instructions carefully</p>
                            <div className={classes.instructioncont}>
                              <p>1. Ensure a stable internet connection, a compatible device, and a quiet environment</p>
                              <p>2. Follow all exam rules — no external help, no switching tabs, no unauthorized materials.</p>
                              <p>3. Manage your time and complete all questions before submission.</p>
                              <p>4. Review your answers and wait for confirmation before exiting.</p>
                            </div>
                            <Grid container spacing={1}>
                              <Grid item lg={6} md={6} sm={12} xs={12}>
                                <div className={`${classes.enroldetails} flex gap-10`}>
                                  <p className={classes.subtitles}>Subject:</p>
                                  <p>{studentAssignment?.subject?.name || "--/--"}</p>
                                </div>
                              </Grid>
                              <Grid item lg={6} md={6} sm={12} xs={12}>
                                <div className={`${classes.enroldetails} flex gap-10`}>
                                  <p className={classes.subtitles}>Total Questions:</p>
                                  <p>{studentAssignment?.questions?.length}</p>
                                </div>
                              </Grid>
                              <Grid item lg={6} md={6} sm={12} xs={12}>
                                <div className={`${classes.enroldetails} flex gap-10`}>
                                  <p className={classes.subtitles}>Marks:</p>
                                  <p>{studentAssignment?.total_grade || "--/--"}</p>
                                </div>
                              </Grid>
                            </Grid>
                          </div>
                          <div className={classes.btns}>
                            <div>
                              <Button
                                onClick={handleNavigate}
                                variant="dangeroutline"
                                buttonName="Cancel"
                                buttonSize="large"
                                color="btndanger"
                              />
                            </div>
                            <div>
                              {!isExamStarted && (
                                <>
                                  {localStorage.getItem("studentAssignmentCache") ? (
                                    <Button
                                      onClick={handleContinueExam}
                                      variant="primary"
                                      buttonName="Continue Assignment"
                                      buttonSize="large"
                                      color="btndefault"
                                    />
                                  ) : (
                                    <Button
                                      onClick={handleStartTest}
                                      variant="primary"
                                      buttonName="Start Assignment"
                                      buttonSize="large"
                                      color="btndefault"
                                    />
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={`${classes.questionmaincont} container-y-overflow`}>
                            {studentAssignment?.questions && studentAssignment?.questions.length > 0 && (
                              studentAssignment?.questions_display_mode === "all_at_once" ? (
                                studentAssignment?.questions.map((item, index) => (
                                  <div key={index} className="margin-bottom-20">
                                    <QuestionCard
                                      id={`question_${index + 1}`}
                                      arrayIndex={index}
                                      questions={studentAssignment?.questions}
                                      item={item}
                                      onAnswerClick={(answer) => handleAnswerClick(
                                        item?.question_index, answer - 1, item?.type)}
                                    />
                                  </div>
                                ))
                              ) : (
                                <QuestionCard
                                  id={`question_${activeQuestionIndex + 1}`}
                                  key={activeQuestionIndex + 1}
                                  arrayIndex={activeQuestionIndex}
                                  questions={studentAssignment?.questions}
                                  item={studentAssignment?.questions[activeQuestionIndex]}
                                  onAnswerClick={(answer) => handleAnswerClick(
                                    studentAssignment?.questions[activeQuestionIndex]?.question_index, answer - 1, studentAssignment?.questions[activeQuestionIndex]?.type)}
                                />
                              )
                            )}
                          </div>
                          <div className={classes.desktopbtns}>
                            <div className={classes.submitbtn}>
                              {studentAssignment?.questions_display_mode !== "all_at_once" && (
                                <Button
                                  onClick={handlePrevious}
                                  variant="primaryoutline"
                                  color="btnfontprimary"
                                  buttonName="Previous"
                                  buttonSize="medium"
                                  disabled={activeQuestionIndex === 0}
                                />
                              )}
                              {(studentAssignment?.questions_display_mode === "all_at_once" || activeQuestionIndex === studentAssignment?.questions.length - 1) ? (
                                <Button
                                  variant="primary"
                                  buttonSize="medium"
                                  color="btndefault"
                                  buttonName="Submit Assignment"
                                  onClick={handleFinalSubmit}
                                />
                              ) : (
                                <Button
                                  onClick={handleNext}
                                  variant="primary"
                                  color="btndefault"
                                  buttonName="Next"
                                  buttonSize="medium"
                                  disabled={activeQuestionIndex === studentAssignment?.questions.length - 1}
                                />
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Grid>
            <div className={classes.mobilescont}>
              <div className={classes.mobiles}>
                {isExamStarted && (
                  <div className={classes.submitbtn}>
                    {studentAssignment?.questions_display_mode !== "all_at_once" && (
                      <Button
                        onClick={handlePrevious}
                        variant="primaryoutline"
                        color="btnfontprimary"
                        buttonName="Previous"
                        buttonSize="medium"
                        disabled={activeQuestionIndex === 0}
                      />
                    )}
                    {(studentAssignment?.questions_display_mode === "all_at_once" || activeQuestionIndex === studentAssignment?.questions.length - 1) ? (
                      <Button
                        variant="primary"
                        buttonSize="medium"
                        color="btndefault"
                        buttonName="Submit Assignment"
                        onClick={handleFinalSubmit}
                      />
                    ) : (
                      <Button
                        onClick={handleNext}
                        variant="primary"
                        color="btndefault"
                        buttonName="Next"
                        buttonSize="medium"
                        disabled={activeQuestionIndex === studentAssignment?.questions.length - 1}
                      />
                    )}
                  </div>
                )}
                {isExamStarted && (
                  <div className={`${classes.questioncont}`}>
                    <h5>QUESTIONS</h5>
                    <Grid container spacing={1}>
                      {studentAssignment && studentAssignment?.questions ? (
                        studentAssignment?.questions.map((queNumber, index) => (
                          <Grid item key={index} lg={2} md={2} sm={1} xs={2}>
                            <p onClick={() => handleNavigateToQuestion(index)}
                              style={{
                                backgroundColor: isQuestionAnswered(queNumber?.question_index) ? "#d4edda" : (activeQuestion === index ? "#F0F2F5" : "transparent"),
                                fontWeight: activeQuestion === index ? "bold" : "normal"
                              }}
                            >{questionDisplayIndex(queNumber, index, studentAssignment?.questions)}
                            </p>
                          </Grid>
                        ))
                      ) : (
                        <p>Please Wait ...</p>
                      )}
                    </Grid>
                  </div>
                )}
              </div>
            </div>
            <Grid item lg={3} md={3} sm={12} xs={12}>
              <div className={`${classes.rightcont}`}>

                <div className={classes.timercont}>
                </div>
                <div className={`${classes.questioncont}`}>
                  <h5>QUESTIONS</h5>
                  <Grid container spacing={1}>
                    {studentAssignment && studentAssignment?.questions ? (
                      studentAssignment?.questions.map((queNumber, index) => (
                        <Grid item key={index} lg={2} md={2} sm={1} xs={2}>
                          <p onClick={() => handleNavigateToQuestion(index)}
                            style={{
                              backgroundColor: isQuestionAnswered(queNumber?.question_index) ? "#d4edda" : (activeQuestion === index ? "#F0F2F5" : "transparent"),
                              fontWeight: activeQuestion === index ? "bold" : "normal"
                            }}
                          >{questionDisplayIndex(queNumber, index, studentAssignment?.questions)}
                          </p>
                        </Grid>
                      ))
                    ) : (
                      <p>Please Wait ...</p>
                    )}
                  </Grid>
                  {/* <Button onClick={handleFinalSubmit} variant="primary" buttonName="Submit Assignment" buttonSize="full" color="btndefault" /> */}
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
      {/* Leave Exam Modal */}
      <Modal
        title="Leave Assignment"
        subtitle={
          <div>
                Are you sure you want to Leave this Assignment?
          </div>
        }
        modalContent={<></>}
        modalFooter={
          <>
            <div className="flex gap-10">
              <Button
                variant="dangeroutline"
                buttonSize="full"
                color="btndanger"
                buttonName="Cancel"
                onClick={handleCloseLeaveModal}
              />
              <Button
                variant="danger"
                buttonName="Leave Assignment"
                buttonSize="full"
                color="btndefault"
                onClick={handleNavigate}
              />
            </div>
          </>
        }
        isopen={leaveModal}
        closeModal={handleCloseLeaveModal}
      />
      {/* <ToastContainer /> */}
    </div>
  );
};

export default EnrolAssignment;
